import { BatchFormFieldType } from 'components/batchForm/BatchForm.helpers';
import {
  arePasteActionKeysPressed,
  doesKeyLooksLikeValidInputForComponent,
  isCtrlKeyPressed,
  isNumber,
  isSpaceBarKeyPressed,
} from 'components/common-form/builder/form/node/field/Grid/cells/utils';
import { BatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { BatchPasteDataContextValue } from 'components/common-form/contexts/batch/BatchPasteDataContext';
import { useLocale } from 'hooks/useLocale';
import type { FieldMetadata } from 'models/forms/fields';
import type { Underlying } from 'models/underlying';
import { Context, KeyboardEvent, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { logAnalytics } from 'shared/middlewares/analytics/logger';

import { handleFieldKeyboardShortcuts } from '../BatchFormNavLayer.keyboardHandlers';
import { BATCH_WORKSPACE } from '../constants';
import { useBatchFormClipboard } from './useBatchFormClipboard';

interface UseKeyboardHandlingProps {
  formId: string;
  type: string;
  value: BatchFormFieldType | undefined;
  setValue: (value: BatchFormFieldType | undefined) => void;
  metadata?: FieldMetadata;
  canEdit: boolean;
  underlyings: ReadonlyArray<Underlying>;
  selectFormCallback: (formId: string) => void;
  setLostInputBeforeEditing: (input: string | undefined) => void;
  navigationContext: typeof BatchNavigationContext extends Context<infer T> ? T : never;
  batchPasteDataContext: BatchPasteDataContextValue;
  cellInteractions: {
    cellId: string;
  };
}

export const useKeyboardHandling = ({
  formId,
  value,
  setValue,
  metadata,
  canEdit,
  underlyings,
  selectFormCallback,
  setLostInputBeforeEditing,
  navigationContext,
  batchPasteDataContext,
  cellInteractions,
}: UseKeyboardHandlingProps) => {
  const intl = useIntl();
  const [locale] = useLocale();
  const { copyToClipboard, readPastedContentClipboard } = useBatchFormClipboard();

  const { setIsEditing, onNavigationKeyDown, setIsSelectAll, lostInputBeforeEditing, defaultUnderlyingCodeType } =
    navigationContext;

  const { setPasteCellId, setPastedGridData, setPastedData } = batchPasteDataContext;

  const isCellEditable = metadata && !metadata.isReadOnly;

  const onKeyDown = useCallback(
    async (e: KeyboardEvent) => {
      if (isCtrlKeyPressed(e) && isSpaceBarKeyPressed(e)) {
        e.preventDefault();
        logAnalytics.trackEvent(BATCH_WORKSPACE, '(Un)select form (Ctrl+Space)');
        selectFormCallback(formId);
        return;
      }
      const isPasting = arePasteActionKeysPressed(e) && !!isCellEditable;
      const keyboardShortcutApplied = metadata
        ? await handleFieldKeyboardShortcuts({
            event: e,
            value,
            metadata,
            isCellEditable: !!isCellEditable,
            isPasting,
            setValue,
            copyToClipboard,
            readPastedContentClipboard,
            cellId: cellInteractions.cellId,
            setPasteCellId,
            setPastedGridData,
            canEdit,
            setIsEditing,
            setIsSelectAll,
            setLostInputBeforeEditing,
            lostInputBeforeEditing,
            underlyings,
            intl,
            locale,
            defaultUnderlyingCodeType,
            setPastedData,
          })
        : false;

      if (keyboardShortcutApplied) {
        return;
      }

      if (onNavigationKeyDown) {
        onNavigationKeyDown(e, canEdit);
        if (metadata && doesKeyLooksLikeValidInputForComponent(e.key, metadata.type) && canEdit && setIsEditing) {
          if (isNumber(e.key)) {
            logAnalytics.trackEvent(BATCH_WORKSPACE, 'Edit numeric field (Numeric key)');
          }

          setLostInputBeforeEditing(e.key);
          setIsEditing(true);
          e.preventDefault();
        }
      }
    },
    [
      formId,
      canEdit,
      onNavigationKeyDown,
      setIsEditing,
      setLostInputBeforeEditing,
      value,
      setValue,
      metadata,
      isCellEditable,
      setIsSelectAll,
      selectFormCallback,
      readPastedContentClipboard,
      copyToClipboard,
      lostInputBeforeEditing,
      intl,
      underlyings,
      locale,
      defaultUnderlyingCodeType,
      cellInteractions.cellId,
      setPasteCellId,
      setPastedGridData,
      setPastedData,
    ],
  );

  return {
    onKeyDown,
  };
};
